import gql from 'graphql-tag';
import {useMemo} from 'react';
import {createSelector} from 'reselect';

import {
  makeRunSetFiltersSelector,
  makeRunSetGroupSelectionsSelector,
  makeRunSetSelector,
} from '../../components/RunSelector/runSetSelectors';
import * as Generated from '../../generated/graphql';
import * as Filter from '../../util/filters';
import {getFiltersForQuery} from '../../util/filterV2';
import * as SelectionManager from '../../util/selectionmanager';
import {useMemoizedSelector} from '../hooks';
import {useRunSetProject} from '../views/runSet/hooks';
import {type Ref as RunSetRef} from '../views/runSet/types';

/**
 * Uses createSelector per https://reselect.js.org/api/createSelector
 * Colocated here since it's specific to RunSetSummary.
 */
const makeRunSetSummaryDataSelector = (runSetRef: RunSetRef) =>
  createSelector(
    [
      makeRunSetSelector(runSetRef),
      makeRunSetFiltersSelector(runSetRef),
      makeRunSetGroupSelectionsSelector(runSetRef),
    ],
    (runSet, filters, groupSelections) => ({
      project: runSet.project,
      filters,
      groupSelections,
    })
  );

///// Query definition

export const RUN_SET_SUMMARY_QUERY = gql`
  query RunSetSummary($projectInternalId: ID!, $selections: JSONString!) {
    project(internalId: $projectInternalId) {
      id
      selectedCount: runCount(filters: $selections)
    }
  }
`;

export interface RunSetSummaryQueryData {
  project: {
    id: string;
    selectedCount: number;
  };
}

export function useRunSetSummaryQuery(runSetRef: RunSetRef) {
  // Use useMemoizedSelector directly with the selector factory instead of the
  // convenience hooks in RunSelectorRunSetContext (e.g., usePinnedAndBaseline).
  // This usePanelSharedProps hook is called from the reports path where that
  // provider doesn't exist.
  const {project, filters, groupSelections} = useMemoizedSelector(
    makeRunSetSummaryDataSelector,
    runSetRef
  );
  const {projectInternalId} = useRunSetProject(project);

  const queryFilters = useMemo(() => getFiltersForQuery(filters), [filters]);
  const queryResults = Generated.useRunSetSummaryQuery({
    variables: {
      projectInternalId: projectInternalId ?? '',
      selections: JSON.stringify(
        Filter.toMongo(
          Filter.And([
            queryFilters,
            SelectionManager.toRunFilter(groupSelections),
          ])
        )
      ),
    },
    skip: projectInternalId == null,
  });

  return {
    ...queryResults,
    initialLoading: queryResults.loading && !queryResults.data,
  };
}
